import React, { useCallback, useEffect, useMemo, useState } from "react";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import ActionButton from "../../../component/common/ActionButton";
import {
    Box, Button, Chip, CircularProgress,
    IconButton, Paper, Popover, Stack, Tooltip, Typography,
} from "@mui/material";
import DetailModal from "../../../component/common/DetailModal";
import SearchField from "../../../component/common/SearchField";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";

import BaseCalendar from "../../../component/calendar/BaseCalendar";

import { notify } from "../../../util/toast";
import AppAutocomplete from "../../../component/common/AppAutocomplete";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";

import { calendarApi } from "../utils/calendarApi";
import { EventDialog } from "./EventDialog";
import { EventDetailModal } from "./EventDetailModal";
import {
    WEEK_DAYS,
    getWeekDays,
    eventOnDay,
    getEventColor,
    isProjectEvent,
    asList,
} from "../utils/calendarUtils";
import {
    CATEGORY_ORDER,
    categoryKeyOf,
    categoryLabelOf,
    categoryMeta,
} from "../constants/eventCategory";

import {
    DATE_FORMAT,
    endOfDayString,
    formatAllDayRange,
    formatDate,
    formatDateKo,
    formatPlainDateRange,
    formatTime,
    sortByStartAt,
    startOfDayString,
    today as getToday,
} from "../../../util/dateUtils";

/* ─────────────────────────────────────────────
 * 휴가 유형 상수
 * - 필터 Chip에 사용
 * - 서버에서 내려오는 leaveType 값과 맞춰야 함
 * ───────────────────────────────────────────── */
const LEAVE_TYPE_OPTIONS = ["연차", "반차", "시간연차", "출장", "병가", "대체휴무"];

/** 유형 체크박스에서 꺼둔 항목 — 새로고침해도 유지되도록 브라우저에 남긴다. */
const HIDDEN_CATS_KEY = "calendar_hidden_categories";

/* ─────────────────────────────────────────────
 * UI 조정값
 * - 날짜 크기 / 이벤트 크기 / 카드 글자 크기를 한 곳에서 관리
 * - 화면이 짤리지 않게 만드는 값이 아니라, 순수 표시 크기 조정값
 * ───────────────────────────────────────────── */
const UI = {
    monthCellH: 138,      // 월간 날짜 칸 최소 높이. 키우면 월간 캘린더 한 칸의 세로 공간이 커짐.
    monthDate: 28,        // 월간 날짜 원 크기. 키우면 날짜 숫자를 감싸는 원이 커짐.
    monthDateFont: 14,    // 월간 날짜 숫자 크기. 키우면 월간 날짜 숫자가 커짐.

    weekDate: 34,         // 주간 날짜 원 크기. 키우면 주간 상단 날짜 원이 커짐.
    weekDateFont: 16,     // 주간 날짜 숫자 크기. 키우면 주간 날짜 숫자가 커짐.

    dayDate: 38,          // 일간 날짜 원 크기. 키우면 일간 헤더 날짜 원이 커짐.
    dayDateFont: 17,      // 일간 날짜 숫자 크기. 키우면 일간 헤더 날짜 숫자가 커짐.

    barFont: 11,          // 월간/주간 일정 바 글자 크기. 키우면 셀 안의 일정 텍스트가 커짐.
    barPy: 0.28,          // 월간/주간 일정 바 세로 여백. 키우면 일정 바 높이가 커짐.
    barPx: 0.75,          // 월간/주간 일정 바 가로 여백. 키우면 일정 바 내부 좌우 여백이 커짐.

    cardTitle: 14,        // 일간/상세/모달 일정 카드 제목 크기. 키우면 카드 제목이 커짐.
    cardMeta: 12,         // 일간/상세/모달 시간/기간 텍스트 크기. 키우면 보조 정보가 커짐.
    cardPy: 1.5,          // 상세/모달 카드 패딩. 키우면 작은 카드 높이가 커짐.
    cardFullPy: 2,        // 일간 카드 패딩. 키우면 일간 뷰 카드 높이가 커짐.
};

/* ─────────────────────────────────────────────
 * 이벤트 바 표시 문자열 생성
 * - EventBar에서 사용
 * - 휴가/출장과 일반 일정을 한 함수에서 처리
 * ───────────────────────────────────────────── */
const eventText = (ev) => {
    if (ev.isLeave) {
        return `${ev.leaveType} · ${ev.employeeName || "미지정"}${ev.deptName ? ` · ${ev.deptName}` : ""}`;
    }
    const timePrefix = !ev.allDay && ev.startAt ? `${formatTime(ev.startAt)} ` : "";
    // 프로젝트 채널 자동 일정 — 프로젝트명만 표시 (PM·팀 생략)
    if (isProjectEvent(ev)) return `${timePrefix}${ev.title}`;
    return `${timePrefix}${ev.title}${ev.employeeName ? ` · ${ev.employeeName}` : ""}${ev.deptName ? ` · ${ev.deptName}` : ""}`;
};

/* 다중일(연속) 일정 여부 — 시작·종료 날짜(YYYY-MM-DD)가 다르면 true */
const isMultiDay = (ev) =>
    !!ev.startAt && !!ev.endAt && String(ev.startAt).slice(0, 10) !== String(ev.endAt).slice(0, 10);

/* ─────────────────────────────────────────────
 * 이벤트 카드 하단 메타 문자열 생성
 * - 휴가: 시작일 ~ 종료일
 * - 종일 일정: 종일 · 날짜
 * - 시간 일정: HH:mm ~ HH:mm
 * ───────────────────────────────────────────── */
const eventMeta = (ev) => {
    if (ev.isLeave) {
        return formatPlainDateRange(ev.startDate, ev.endDate);
    }

    if (ev.allDay) {
        return formatAllDayRange(ev.startAt);
    }

    return ev.startAt
        ? `${formatTime(ev.startAt)}${ev.endAt ? ` ~ ${formatTime(ev.endAt)}` : ""}`
        : "";
};

/* ─────────────────────────────────────────────
 * 날짜별 이벤트 정렬 함수
 * - 월간/주간/일간에서 반복되던 로직 통합
 * - 필터 활성 시 강조 대상이 위로 오고, 비강조 대상은 아래로 이동
 * ───────────────────────────────────────────── */
const sortedDayEvents = (events, day, isDimmed, filterActive) => {
    const arr = events
        .filter(ev => eventOnDay(ev, day))
        .sort(sortByStartAt);

    return filterActive
        ? [...arr.filter(ev => !isDimmed(ev)), ...arr.filter(ev => isDimmed(ev))]
        : arr;
};

/* ─────────────────────────────────────────────
 * 이벤트 Chip 공통 스타일
 * - 직원명 Chip / 휴가유형 Chip에서 사용
 * ───────────────────────────────────────────── */
const chipSx = (c, fontSize = 10) => ({
    bgcolor: c.bg,
    color: c.text,
    fontWeight: 700,
    fontSize,
    borderRadius: 1,
});

/* ─────────────────────────────────────────────
 * 월간/주간 셀 안에 표시되는 작은 이벤트 바
 * ───────────────────────────────────────────── */
function EventBar({ ev, onClick, compact = false, dimmed = false }) {
    const c = getEventColor(ev);

    // Tooltip에는 상세 정보를 짧게 표시
    const tooltipTitle = ev.isLeave
        ? [ev.submitterName || ev.employeeName, ev.leaveType, ev.deptName].filter(Boolean).join(" · ")
        : isProjectEvent(ev)
            ? ev.title  // 프로젝트 일정 — 프로젝트명만
            : [
                ev.employeeName || "미지정",
                ev.createdByName ? `등록: ${ev.createdByName}` : "",
                ev.deptName ? `팀: ${ev.deptName}` : "",
            ].filter(Boolean).join(" · ");

    return (
        <Tooltip title={tooltipTitle} placement="top" arrow>
            <Box
                onClick={(e) => {
                    // 날짜 셀 클릭 이벤트로 전파되지 않게 차단
                    e.stopPropagation();
                    onClick(ev);
                }}
                sx={{
                    px: UI.barPx,                                  // 일정 바 좌우 여백. 키우면 바 내부 여백 증가.
                    py: compact ? UI.barPy : UI.barPy + 0.1,       // 일정 바 세로 여백. 키우면 바 높이 증가.
                    borderRadius: 0.6,
                    bgcolor: dimmed ? "#f1f5f9" : c.bg,
                    borderLeft: `3px solid ${dimmed ? "#cbd5e1" : c.bar}`,
                    cursor: "pointer",
                    opacity: dimmed ? 0.45 : 1,
                    transition: "opacity 0.15s",
                    minWidth: 0,
                    "&:hover": { filter: "brightness(0.95)" },
                }}
            >
                <Typography
                    sx={{
                        fontSize: UI.barFont,                       // 월/주 일정 바 글자 크기. 키우면 일정 텍스트가 커짐.
                        fontWeight: 700,
                        color: dimmed ? "#94a3b8" : c.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1.45,
                    }}
                >
                    {eventText(ev)}
                </Typography>
            </Box>
        </Tooltip>
    );
}

/* ─────────────────────────────────────────────
 * 일간/상세패널/모달에서 사용하는 공통 이벤트 카드
 * - 기존 DayView, DayDetailPanel, DayListModal에 중복되던 카드 JSX 제거
 * ───────────────────────────────────────────── */
function EventCard({ ev, onClick, dimmed = false, dense = false }) {
    const c = getEventColor(ev);
    const meta = eventMeta(ev);

    return (
        <Box
            onClick={() => onClick(ev)}
            sx={{
                p: dense ? UI.cardPy : UI.cardFullPy,              // 카드 패딩. 키우면 카드 높이와 내부 여백 증가.
                borderRadius: dense ? 1.5 : 2,
                border: "1px solid #e2e8f0",
                borderLeft: `${dense ? 3 : 4}px solid ${dimmed ? "#cbd5e1" : c.bar}`,
                cursor: "pointer",
                opacity: dimmed ? 0.4 : 1,
                transition: "opacity 0.15s, background 0.15s",
                minWidth: 0,
                "&:hover": { bgcolor: "#f8fafc" },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: dense ? 13 : UI.cardTitle,       // 카드 제목 크기. 키우면 일정 제목이 커짐.
                        color: "#1e293b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                    }}
                >
                    {ev.title}
                </Typography>

                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                    {/* 유형 배지 — 목록에서 색만으로는 무슨 유형인지 알기 어려워 이름을 함께 보여준다.
                        휴가는 아래 leaveType 칩이 같은 말을 하므로 중복 표기하지 않는다. */}
                    {!ev.isLeave && (
                        <Chip label={categoryLabelOf(ev)} size="small"
                            sx={{ ...chipSx(c, dense ? 10 : 11), border: `1px solid ${c.bar}33` }} />
                    )}
                    {ev.isLeave && (
                        <Chip label={ev.leaveType} size="small" sx={chipSx(c, dense ? 10 : 11)} />
                    )}
                    {/* 프로젝트 일정은 PM 이름 칩 생략 — 프로젝트명만 노출 */}
                    {!isProjectEvent(ev) && (
                        <Chip label={ev.employeeName || "미지정"} size="small" sx={chipSx(c, dense ? 10 : 11)} />
                    )}
                </Stack>
            </Stack>

            {meta && (
                <Typography
                    sx={{
                        fontSize: dense ? 11 : UI.cardMeta,         // 카드 보조 정보 크기. 키우면 시간/기간 텍스트가 커짐.
                        color: "#64748b",
                        mt: 0.4,
                    }}
                >
                    {meta}
                </Typography>
            )}
        </Box>
    );
}

/* MonthView는 BaseCalendar로 대체됨 — renderDayBadges / renderDaySuffix로 주입 */

/* ─────────────────────────────────────────────
 * 주간 뷰
 * - 7일을 동일 폭으로 표시
 * - minmax(0, 1fr)로 내부 텍스트 때문에 가로가 밀리는 문제 방지
 * ───────────────────────────────────────────── */
function WeekView({ anchor, events, onEventClick, onAddClick, isDimmed, filterActive }) {
    const days = useMemo(() => getWeekDays(anchor), [anchor]);
    const today = getToday();

    return (
        <Paper elevation={0} sx={{ width: "100%", minWidth: 0, border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", width: "100%", minWidth: 0 }}>
                {days.map((day, i) => {
                    const isToday = day.isSame(today, "day");
                    const dow = day.day();

                    // 날짜별 이벤트 추출 + 정렬 + 필터 강조 순서 적용
                    const dayEvs = sortedDayEvents(events, day, isDimmed, filterActive);

                    return (
                        <Box key={i} sx={{ minWidth: 0, borderRight: i !== 6 ? "1px solid #f1f5f9" : "none" }}>
                            {/* 주간 날짜 헤더 */}
                            <Box
                                onClick={() => onAddClick(day)}
                                sx={{
                                    py: 1.2,
                                    textAlign: "center",
                                    bgcolor: "#f8fafc",
                                    borderBottom: "1px solid #e2e8f0",
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "#f1f5f9" },
                                }}
                            >
                                <Typography sx={{ fontSize: 11, color: dow === 0 ? "#ef4444" : dow === 6 ? "#3b82f6" : "#64748b", fontWeight: 700 }}>
                                    {WEEK_DAYS[dow]}
                                </Typography>

                                {/* 주간 날짜 원 */}
                                <Box
                                    sx={{
                                        width: UI.weekDate,             // 주간 날짜 원 크기. 키우면 주간 날짜 원이 커짐.
                                        height: UI.weekDate,            // 주간 날짜 원 크기. width와 같이 조정.
                                        borderRadius: "50%",
                                        mx: "auto",
                                        mt: 0.3,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: isToday ? "#2563eb" : "transparent",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: UI.weekDateFont,   // 주간 날짜 숫자 크기. 키우면 주간 날짜 숫자가 커짐.
                                            fontWeight: isToday ? 700 : 600,
                                            color: isToday ? "#fff" : dow === 0 ? "#ef4444" : dow === 6 ? "#3b82f6" : "#1e293b",
                                        }}
                                    >
                                        {day.date()}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* 주간 일정 목록 */}
                            <Stack spacing={0.4} sx={{ p: 0.6, minHeight: 130, minWidth: 0 }}>
                                {dayEvs.map(ev => (
                                    <EventBar key={ev.id} ev={ev} onClick={onEventClick} dimmed={isDimmed(ev)} />
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}

/* ─────────────────────────────────────────────
 * 일간 뷰
 * - 하루 일정 전체를 카드 리스트로 표시
 * ───────────────────────────────────────────── */
function DayView({ anchor, events, onEventClick, onAddClick, isDimmed, filterActive }) {
    const today = getToday();
    const isToday = anchor.isSame(today, "day");

    // 날짜별 이벤트 정렬 로직 공통화
    const dayEvs = useMemo(
        () => sortedDayEvents(events, anchor, isDimmed, filterActive),
        [events, anchor, filterActive, isDimmed],
    );

    return (
        <Paper elevation={0} sx={{ width: "100%", minWidth: 0, border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            {/* 일간 헤더 */}
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: UI.dayDate,              // 일간 날짜 원 크기. 키우면 일간 헤더 날짜 원이 커짐.
                            height: UI.dayDate,             // 일간 날짜 원 크기. width와 같이 조정.
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: isToday ? "#2563eb" : "#e2e8f0",
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: UI.dayDateFont,    // 일간 날짜 숫자 크기. 키우면 일간 날짜 숫자가 커짐.
                                fontWeight: 700,
                                color: isToday ? "#fff" : "#1e293b",
                            }}
                        >
                            {anchor.date()}
                        </Typography>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                            {formatDateKo(anchor)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                            {anchor.format("dddd")} {isToday && "• 오늘"}
                        </Typography>
                    </Box>
                </Stack>

                <ActionButton
                    variant="secondary"
                    startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onAddClick(anchor)}
                    label="일정 추가"
                    sx={{ flexShrink: 0 }}
                />
            </Box>

            {/* 일간 일정 목록 */}
            <Box sx={{ p: 2 }}>
                {dayEvs.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 5, color: "#94a3b8" }}>
                        <CalendarMonthRoundedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
                        <Typography sx={{ fontSize: 13 }}>이 날 등록된 일정이 없습니다</Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {dayEvs.map(ev => (
                            <EventCard key={ev.id} ev={ev} onClick={onEventClick} dimmed={isDimmed(ev)} />
                        ))}
                    </Stack>
                )}
            </Box>
        </Paper>
    );
}

/* ─────────────────────────────────────────────
 * +N개 일정 모달
 * - 월간 셀에서 표시되지 않은 일정 목록 표시
 * ───────────────────────────────────────────── */
function DayListModal({ open, day, events, onClose, onEventClick, onAddClick }) {
    if (!day) return null;

    return (
        <DetailModal
            open={open}
            onClose={onClose}
            title={day.format("M월 D일 (ddd)")}
            subtitle={`${events.length}건`}
            maxWidth="xs"
        >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <ActionButton
                    variant="secondary"
                    startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => { onClose(); onAddClick(day); }}
                    label="추가"
                    sx={{ fontSize: 12 }}
                />
            </Box>

            {events.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 3 }}>
                    이 날 일정이 없습니다
                </Typography>
            ) : (
                <Stack spacing={0.75}>
                    {[...events].sort(sortByStartAt).map(ev => (
                        <EventCard key={ev.id} ev={ev} onClick={onEventClick} dense />
                    ))}
                </Stack>
            )}
        </DetailModal>
    );
}

/* ─────────────────────────────────────────────
 * 캘린더 메인 컴포넌트
 * - API 호출
 * - 필터 상태
 * - 뷰 전환
 * - 이벤트 클릭 처리
 * ───────────────────────────────────────────── */
export default function CalendarView({ view, setView, me, onMyClick }) {
    /* 기준 날짜 */
    const [anchor, setAnchor] = useState(getToday());

    /* 일반 일정 / 휴가 일정 */
    const [events, setEvents] = useState([]);
    const [leaveEvents, setLeaveEvents] = useState([]);
    const [channelEvents, setChannelEvents] = useState([]); // 프로젝트 채널 라이브 조회(SSOT)

    /* 로딩 상태 */
    const [loading, setLoading] = useState(false);

    /* 필터 옵션 */
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);

    /* 선택된 필터 */
    const [filterEmps, setFilterEmps] = useState([]);
    const [filterDepts, setFilterDepts] = useState([]);
    const [filterLeaveTypes, setFilterLeaveTypes] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [filterAnchor, setFilterAnchor] = useState(null);   // '유형 필터' 팝오버 앵커

    /* UI 상태 */
    const [selectedDay, setSelectedDay] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);   // 등록·수정 폼
    const [detailOpen, setDetailOpen] = useState(false);   // 읽기전용 상세(클릭하면 먼저 이게 열린다)
    const [dialogEvent, setDialogEvent] = useState(null);  // 상세·폼이 공유하는 대상 이벤트
    const [dayListModal, setDayListModal] = useState(null);

    /* 뷰가 바뀌면 날짜 상세 패널 닫기 */
    useEffect(() => {
        setSelectedDay(null);
    }, [view]);

    /* 직원/부서 필터 옵션 로드 */
    useEffect(() => {
        calendarApi.getEmployees()
            .then(r => setEmployees(asList(r, "employees")))
            .catch(() => notify("직원 목록 로드 실패 — 직원 필터 사용 불가", "error"));
        calendarApi.getDepartments()
            .then(r => setDepartments(asList(r, "departments")))
            .catch(() => notify("부서 목록 로드 실패 — 팀 필터 사용 불가", "error"));
    }, []);

    /* 현재 뷰 기준 조회 기간 계산 */
    const { from, to } = useMemo(() => {
        if (view === "month") {
            return {
                from: anchor.startOf("month").subtract(7, "day"),
                to: anchor.endOf("month").add(7, "day"),
            };
        }

        if (view === "week") {
            return {
                from: anchor.startOf("week"),
                to: anchor.endOf("week"),
            };
        }

        return {
            from: anchor.startOf("day"),
            to: anchor.endOf("day"),
        };
    }, [view, anchor]);

    /* 일반 일정 + 휴가 일정 합산 */
    /**
     * 유형 on/off — 꺼진 유형 키 집합. 여기 담긴 유형은 <b>흐려지는 게 아니라 아예 사라진다</b>.
     *
     * <p>기존 직원·부서·검색 필터는 '해당 없는 건 흐리게(dim)' 방식이라 화면이 여전히 빽빽했다.
     * 유형 체크박스는 "회의만 보고 싶다"는 요구라 실제로 걷어내야 의미가 있어 성격을 다르게 둔다.
     * 선택은 localStorage 에 남겨 새로고침·재방문해도 유지한다(매번 다시 끄는 게 더 번거롭다).
     */
    const [hiddenCats, setHiddenCats] = useState(() => {
        try {
            const raw = localStorage.getItem(HIDDEN_CATS_KEY);
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });

    const toggleCat = useCallback((key) => {
        setHiddenCats(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            try { localStorage.setItem(HIDDEN_CATS_KEY, JSON.stringify([...next])); } catch { /* 저장 실패는 무시 */ }
            return next;
        });
    }, []);

    const setAllCats = useCallback((on) => {
        const next = on ? new Set() : new Set(CATEGORY_ORDER);
        try { localStorage.setItem(HIDDEN_CATS_KEY, JSON.stringify([...next])); } catch { /* 무시 */ }
        setHiddenCats(next);
    }, []);

    const allEvents = useMemo(
        () => [...events, ...leaveEvents, ...channelEvents],
        [events, leaveEvents, channelEvents],
    );

    /**
     * 유형 체크박스가 켜진 일정만. 이 아래 모든 렌더(월/주/일 · 날짜 모달 · 다중일 막대)는
     * allEvents 가 아니라 <b>이 값</b>을 써야 한다 — 한 군데라도 allEvents 를 쓰면 "체크를 껐는데
     * 저기서만 보인다"가 된다.
     */
    const shownEvents = useMemo(
        () => (hiddenCats.size === 0 ? allEvents : allEvents.filter(ev => !hiddenCats.has(categoryKeyOf(ev)))),
        [allEvents, hiddenCats],
    );

    /** 유형별 건수 — 체크박스 옆에 뜨는 숫자. 끈 유형도 원래 몇 건인지 보여야 하므로 allEvents 기준. */
    const catCounts = useMemo(() => {
        const m = {};
        allEvents.forEach(ev => { const k = categoryKeyOf(ev); m[k] = (m[k] || 0) + 1; });
        return m;
    }, [allEvents]);


    /* 필터 활성 여부 */
    const filterTextTrimmed = filterText.trim();
    const filterActive = filterEmps.length > 0 || filterDepts.length > 0
        || filterLeaveTypes.length > 0 || filterTextTrimmed.length > 0;

    /* 필터 조건에 해당하는 이벤트 ID 집합 */
    const highlightedIds = useMemo(() => {
        if (!filterActive) return null;

        const ids = new Set();
        const q = filterTextTrimmed.toLowerCase();

        const matchesText = (ev) => {
            if (!q) return false;
            const fields = [ev.title, ev.employeeName, ev.deptName, ev.leaveType, ev.description];
            return fields.some(f => typeof f === "string" && f.toLowerCase().includes(q));
        };

        allEvents.forEach(ev => {
            if (filterEmps.some(e => e.id === ev.employeeId)) {
                ids.add(ev.id);
                return;
            }

            if (filterDepts.some(d => d.deptId === ev.deptId)) {
                ids.add(ev.id);
                return;
            }

            if (filterLeaveTypes.length && ev.isLeave && filterLeaveTypes.includes(ev.leaveType)) {
                ids.add(ev.id);
                return;
            }

            if (matchesText(ev)) {
                ids.add(ev.id);
            }
        });

        return ids;
    }, [allEvents, filterActive, filterEmps, filterDepts, filterLeaveTypes, filterTextTrimmed]);

    /* 필터에서 제외되는 이벤트는 흐리게 표시 */
    const isDimmed = useCallback(
        (ev) => highlightedIds !== null && !highlightedIds.has(ev.id),
        [highlightedIds],
    );


    /* 일정 로드 */
    const loadEvents = useCallback(async () => {
        setLoading(true);

        const fromStr = from.format(DATE_FORMAT.datetimeSecondInput);
        const toStr = to.format(DATE_FORMAT.datetimeSecondInput);

        const [evRes, lvRes, chRes] = await Promise.allSettled([
            calendarApi.getEvents(fromStr, toStr, [], []),
            calendarApi.getLeaveEvents(formatDate(from), formatDate(to), [], [], []),
            calendarApi.getChannelEvents(formatDate(from), formatDate(to)),   // 프로젝트 채널 라이브 조회(SSOT)
        ]);

        if (evRes.status === "fulfilled") {
            const evData = evRes.value;
            setEvents(asList(evData));
        } else {
            notify("일정 불러오기 실패", "error");
        }

        if (lvRes.status === "fulfilled") {
            const lvData = lvRes.value;
            const rawLeaves = asList(lvData);
            const leaves = rawLeaves.map(l => ({
                id: `leave-${l.approvalId}`,
                title: l.title,
                employeeId: l.submitterId,
                employeeName: l.submitterName,
                deptId: l.deptId,
                deptName: l.deptName,
                startAt: startOfDayString(l.startDate),
                endAt: endOfDayString(l.endDate),
                allDay: true,
                isLeave: true,
                leaveType: l.leaveType,
                startDate: l.startDate,
                endDate: l.endDate,
                approvalId: l.approvalId,
            }));

            setLeaveEvents(leaves);
        } else {
            setLeaveEvents([]);
        }

        // 프로젝트 채널 — 휴가와 동일하게 읽기 시점 라이브 조회 결과를 합성 이벤트로 매핑(SSOT, 복사본 없음).
        // 채널명·기간 수정이 즉시 반영되고 채널 삭제 시 자동으로 사라진다.
        if (chRes.status === "fulfilled") {
            const chData = chRes.value;
            const rawChannels = asList(chData);
            const channels = rawChannels.map(c => ({
                id: `channel-${c.channelId}`,
                channelId: c.channelId,
                projectChannelId: c.channelId, // 기존 프로젝트 스타일(제목만·PM칩 생략) 재사용
                isProject: true,
                title: c.title,
                employeeId: c.pmId,
                employeeName: c.pmName,
                deptId: c.deptId,
                deptName: c.deptName,
                startAt: startOfDayString(c.startDate),
                endAt: endOfDayString(c.endDate),
                allDay: true,
                category: "PLANNING_EVENT",
            }));
            setChannelEvents(channels);
        } else {
            setChannelEvents([]);
        }

        setLoading(false);
    }, [from, to]);

    /* 조회 기간이 바뀌면 일정 재조회 */
    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    /* 월간 날짜 클릭 — 그 날짜의 일정 전체를 모달로 연다.
       (예전에는 캘린더 아래 패널에 폈는데, 달력이 길면 패널이 화면 밖이라 스크롤해야 보였다.) */
    const handleDayClick = (day) => {
        setSelectedDay(day);
        setDayListModal({ day, events: shownEvents.filter(ev => eventOnDay(ev, day)) });
    };

    /* +N개 일정 클릭 */
    const handleMoreClick = (day, dayEvs) => {
        setDayListModal({ day, events: dayEvs });
    };

    /* 이벤트 클릭 — 먼저 '읽기전용 상세'를 연다.
       일정은 보는 일이 고치는 일보다 훨씬 잦은데 예전엔 클릭하자마자 등록·수정 폼이 떠서,
       내용만 확인하려다 값을 건드릴 위험이 있었다. 고치려면 상세에서 [수정]을 한 번 더 누른다.
       합성 이벤트(휴가/프로젝트)는 캘린더가 소유하지 않으므로 항상 수정 불가(_isOwner=false). */
    const handleEventClick = (ev) => {
        const owner = (ev.isLeave || isProjectEvent(ev))
            ? false
            : (me ? (ev.createdById === me.id || ev.employeeId === me.id) : true);
        setDialogEvent({ ...ev, _isOwner: owner });
        setDetailOpen(true);
        setDayListModal(null);
    };

    /* 상세에서 [수정] — 같은 이벤트를 그대로 들고 기존 등록·수정 폼으로 넘긴다. */
    const handleEditFromDetail = () => {
        setDetailOpen(false);
        setDialogOpen(true);
    };

    /* 일정 추가 */
    const handleAddClick = (day) => {
        setDialogEvent({ _defaultDate: day.toDate() });
        setDialogOpen(true);
    };

    /* 일정 저장 */
    const handleSave = async (id, payload) => {
        if (id) {
            await calendarApi.update(id, payload);
            return id;
        }

        const res = await calendarApi.create(payload);
        return (res?.data ?? res)?.id;
    };

    /* 일정 삭제 */
    const handleDelete = async (id) => {
        if (!(await confirmDialog({ message: "일정을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => calendarApi.delete(id),
            successMsg: "삭제되었습니다",
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: () => { setDialogOpen(false); loadEvents(); },
        });
    };

    /* 휴가 유형 필터 토글 */
    const toggleLeaveType = (type) => {
        setFilterLeaveTypes(p => p.includes(type) ? p.filter(t => t !== type) : [...p, type]);
    };

    /* 월간 연속 바 — 다중일 이벤트만 BaseCalendar 의 spans 로 전달하고, 단일일은 셀 배지로 표시 */
    const singleDayEvents = shownEvents.filter((ev) => !isMultiDay(ev));
    const spans = shownEvents.filter(isMultiDay).map((ev) => {
        const c = getEventColor(ev);
        return {
            id: ev.id,
            start: ev.startAt,
            end: ev.endAt,
            label: eventText({ ...ev, allDay: true }),
            bg: c.bg, bar: c.bar, text: c.text,
            dimmed: isDimmed(ev),
            onClick: () => handleEventClick(ev),
        };
    });

    // '유형 필터' 팝오버 — 활성 그룹 수(일정 유형 숨김 여부 + 결재 유형 선택 여부).
    // 현금흐름표 필터의 activeRangeCount 와 같은 의미: 켜진 필터 그룹 개수를 버튼에 뱃지로 표시.
    const activeFilterCount = (hiddenCats.size > 0 ? 1 : 0) + (filterLeaveTypes.length > 0 ? 1 : 0);

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            {/* 필터 영역 */}
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid #e2e8f0", mb: 1.5, width: "100%", minWidth: 0 }}>
                <Stack spacing={1}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                        {/* 직원 필터 */}
                        <AppAutocomplete
                            multiple
                            options={employees}
                            getOptionLabel={o => o.name || ""}
                            secondaryText={o => o.deptName || ""}
                            value={filterEmps}
                            onChange={(v) => {
                                setFilterEmps(v);
                                setSelectedDay(null);
                            }}
                            label="직원 필터" placeholder="직원 선택..."
                            variant="outlined" disableUnderline={false}
                            sx={{ flex: 1, minWidth: 0 }}
                        />

                        {/* 팀 필터 */}
                        <AppAutocomplete
                            multiple
                            options={departments}
                            getOptionLabel={o => o.deptName || ""}
                            value={filterDepts}
                            onChange={(v) => {
                                setFilterDepts(v);
                                setSelectedDay(null);
                            }}
                            label="팀 필터" placeholder="팀 선택..."
                            variant="outlined" disableUnderline={false}
                            sx={{ flex: 1, minWidth: 0 }}
                        />

                        {/* 일정 검색 — 시스템 공통 표 검색과 동일한 디자인(SearchField).
                            '유형 필터' 버튼은 사용자 요청으로 캘린더 툴바의 '일정 추가' 옆으로 옮겼다(아래 toolbarRight). */}
                        <SearchField
                            value={filterText}
                            onChange={(v) => { setFilterText(v); setSelectedDay(null); }}
                            placeholder="제목·담당자·팀·내용 검색..."
                            width="auto"
                            sx={{ flex: 1.2, minWidth: 0 }}
                        />
                    </Stack>

                    {/*
                      활성화된 필터만 요약해서 보여준다 — 전체 유형을 항상 나열하지 않고(불편함 해소),
                      실제로 켜진(숨긴 일정 유형 / 선택한 결재 유형) 것만 칩으로. 전체 토글은 '유형 필터' 팝오버.
                      아무 필터도 활성이 아니면 이 줄 자체를 렌더하지 않는다.
                    */}
                    {(hiddenCats.size > 0 || filterLeaveTypes.length > 0) && (
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            {hiddenCats.size > 0 && (
                                <Chip
                                    size="small"
                                    label={`일정 유형 ${CATEGORY_ORDER.length - hiddenCats.size}/${CATEGORY_ORDER.length}`}
                                    onClick={(e) => setFilterAnchor(e.currentTarget)}
                                    onDelete={() => { setAllCats(true); setSelectedDay(null); }}
                                    sx={{
                                        borderRadius: 1, fontSize: 11, fontWeight: 700, height: 24, cursor: "pointer",
                                        color: "#475569", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0",
                                    }}
                                />
                            )}
                            {filterLeaveTypes.map(type => (
                                <Chip
                                    key={type}
                                    size="small"
                                    label={type}
                                    onDelete={() => toggleLeaveType(type)}
                                    sx={{
                                        borderRadius: 1, fontSize: 11, fontWeight: 700, height: 24,
                                        color: "#713f12", bgcolor: "#fef9c3", border: "1px solid #ca8a04",
                                        "& .MuiChip-deleteIcon": { color: "#ca8a04", "&:hover": { color: "#a16207" } },
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
            </Paper>

            {/* 유형 필터 팝오버 — 현금흐름표 필터와 동일 패턴. 일정 유형(하드 숨김) + 결재 유형(강조 매칭). */}
            <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <Box sx={{ p: 2, width: 340 }}>
                    <Stack spacing={1.8}>
                        {/* 일정 유형 — 끄면 캘린더에서 아예 숨김(하드 필터). 칩에 유형별 건수 표시. */}
                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>일정 유형</Typography>
                                    <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>· 끄면 캘린더에서 숨김</Typography>
                                </Stack>
                                <Typography
                                    onClick={() => { setAllCats(hiddenCats.size > 0); setSelectedDay(null); }}
                                    sx={{ fontSize: 11, color: "#94a3b8", cursor: "pointer", "&:hover": { color: "#64748b" } }}
                                >
                                    {hiddenCats.size > 0 ? "전체 켜기" : "전체 끄기"}
                                </Typography>
                            </Stack>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                                {CATEGORY_ORDER.map(key => {
                                    const meta = categoryMeta(key);
                                    const on = !hiddenCats.has(key);
                                    const count = catCounts[key] || 0;
                                    return (
                                        <Chip
                                            key={key}
                                            size="small"
                                            label={`${meta.label} ${count}`}
                                            onClick={() => { toggleCat(key); setSelectedDay(null); }}
                                            sx={{
                                                cursor: "pointer", fontSize: 11, fontWeight: 700, height: 26, borderRadius: 1,
                                                color: on ? meta.text : "#94a3b8",
                                                bgcolor: on ? meta.bg : "transparent",
                                                border: `1px solid ${on ? meta.bar : "#e2e8f0"}`,
                                                "&:hover": { bgcolor: on ? meta.bg : "#f8fafc", borderColor: meta.bar },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* 결재 유형 — 결재 완료된 일정만 매칭(강조/흐림). */}
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.6 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>결재 유형</Typography>
                                <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>· 결재 완료된 일정만 매칭</Typography>
                            </Stack>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                                {LEAVE_TYPE_OPTIONS.map(type => {
                                    const selected = filterLeaveTypes.includes(type);
                                    return (
                                        <Chip
                                            key={type}
                                            size="small"
                                            label={type}
                                            onClick={() => toggleLeaveType(type)}
                                            sx={{
                                                cursor: "pointer", fontSize: 11, fontWeight: 700, height: 26, borderRadius: 1,
                                                color: selected ? "#713f12" : "#94a3b8",
                                                bgcolor: selected ? "#fef9c3" : "transparent",
                                                border: `1px solid ${selected ? "#ca8a04" : "#e2e8f0"}`,
                                                "&:hover": { bgcolor: "#fef9c3", color: "#713f12", borderColor: "#ca8a04" },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.6 }}>
                                · 휴가·출장은 결재 완료 시 자동 추가
                            </Typography>
                        </Box>

                        {/* 푸터 — 초기화 / 닫기 */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 0.5 }}>
                            <Button
                                size="small"
                                onClick={() => { setAllCats(true); setFilterLeaveTypes([]); setSelectedDay(null); }}
                                disabled={activeFilterCount === 0}
                                sx={{ fontSize: 12, textTransform: "none", color: "#64748b" }}
                            >
                                초기화
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => setFilterAnchor(null)}
                                sx={{ fontSize: 12, textTransform: "none" }}
                            >
                                닫기
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Popover>

            {/* BaseCalendar: 네비게이션 + 월간 그리드 (주간/일간은 아래에서 직접 렌더링) */}
            <BaseCalendar
                anchor={anchor}
                onAnchorChange={(d) => { setAnchor(d); setSelectedDay(null); }}
                view={view}
                onViewChange={(v) => { if (setView) setView(v); }}
                cellHeight={UI.monthCellH}
                selectedDay={selectedDay}
                onDayClick={handleDayClick}
                loading={loading}
                onRefresh={loadEvents}
                spans={spans}
                toolbarLeft={
                    <Box sx={{ ml: 0.5 }}>
                        <ActionButton
                            variant="secondary"
                            startIcon={<FormatListBulletedRoundedIcon sx={{ fontSize: 13 }} />}
                            onClick={onMyClick}
                            label="내 일정"
                            sx={{ px: 1.5, py: 0.4, fontSize: 12,
                                border: "1px solid #99f6e4", color: "#0f766e",
                                "&:hover": { bgcolor: "#f0fdf4", borderColor: "#6ee7b7" } }}
                        />
                    </Box>
                }
                toolbarRight={
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/*
                          색상 범례는 두지 않는다 — '유형 필터' 팝오버가 각 유형의 색과 이름을 그대로 보여주므로
                          범례를 따로 두면 같은 정보가 두 곳에 생기고, 한쪽만 고치면 거짓말이 된다. 실제로 예전
                          범례(개인/팀/프로젝트/휴가·출장)는 색 규칙이 유형 기준으로 바뀐 뒤에도 그대로 남아
                          화면의 색과 정반대의 설명을 하고 있었다.

                          유형 필터 — 사용자 요청으로 상단 필터 줄에서 여기('일정 추가' 옆)로 옮겼다.
                        */}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterListRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={(e) => setFilterAnchor(e.currentTarget)}
                            sx={{
                                textTransform: "none", fontSize: 12, height: 36, flexShrink: 0, whiteSpace: "nowrap",
                                borderColor: activeFilterCount ? "#2563eb" : "rgba(0,0,0,0.23)",
                                color: activeFilterCount ? "#2563eb" : "#475569",
                                bgcolor: activeFilterCount ? "#eff6ff" : "transparent",
                            }}
                        >
                            유형 필터{activeFilterCount ? ` ${activeFilterCount}` : ""}
                        </Button>
                        <ActionButton
                            variant="add"
                            onClick={() => { setDialogEvent(null); setDialogOpen(true); }}
                            label="일정 추가"
                        />
                    </Stack>
                }
                renderDaySuffix={(day, { isCurrentMonth }) => isCurrentMonth ? (
                    <Box
                        onClick={(e) => { e.stopPropagation(); handleAddClick(day); }}
                        sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                            borderRadius: "50%", color: "#94a3b8", opacity: 0, flexShrink: 0,
                            "&:hover": { bgcolor: "#e2e8f0", opacity: "1 !important" },
                            ".MuiBox-root:hover &": { opacity: 1 } }}
                    >
                        <AddRoundedIcon sx={{ fontSize: 13 }} />
                    </Box>
                ) : null}
                renderDayBadges={(day) => {
                    const MAX = 3;
                    const dayEvs = sortedDayEvents(singleDayEvents, day, isDimmed, filterActive);
                    // 셀에는 강조 + 흐림 모두 표시 (정렬 상 강조가 위, 흐림이 아래).
                    // +N 카운트는 전체 기준으로 더 명확하게 (강조든 흐림이든 더 있으면 보여줌).
                    const overflowCount = Math.max(0, dayEvs.length - MAX);
                    return (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                            {dayEvs.slice(0, MAX).map(ev => (
                                <EventBar key={ev.id} ev={ev} onClick={handleEventClick} compact dimmed={isDimmed(ev)} />
                            ))}
                            {overflowCount > 0 && (
                                <Typography
                                    onClick={(e) => { e.stopPropagation(); handleMoreClick(day, dayEvs); }}
                                    sx={{ fontSize: 10, color: "#2563eb", pl: 0.3, fontWeight: 700,
                                        cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                >
                                    +{overflowCount}개의 일정
                                </Typography>
                            )}
                        </Stack>
                    );
                }}
            />

            {/* 주간/일간/상세 — BaseCalendar가 month 외에는 null을 반환하므로 여기서 직접 렌더링 */}
            <Box sx={{ width: "100%", minWidth: 0, overflowX: "hidden" }}>
                <Box sx={{ width: "100%", minWidth: 0 }}>
                    {/* 월간 날짜 클릭은 이제 모달(DayListModal)로 연다 — 하단 패널은 캘린더가 길면
                        화면 밖에 생겨 스크롤해야 보였다. 아래 DayListModal 참고. */}

                    {view === "week" && (
                        <WeekView
                            anchor={anchor}
                            events={shownEvents}
                            onEventClick={handleEventClick}
                            onAddClick={handleAddClick}
                            isDimmed={isDimmed}
                            filterActive={filterActive}
                        />
                    )}

                    {view === "day" && (
                        <DayView
                            anchor={anchor}
                            events={shownEvents}
                            onEventClick={handleEventClick}
                            onAddClick={handleAddClick}
                            isDimmed={isDimmed}
                            filterActive={filterActive}
                        />
                    )}
                </Box>
            </Box>

            {/* +N개 일정 모달 */}
            <DayListModal
                open={Boolean(dayListModal)}
                day={dayListModal?.day}
                events={dayListModal?.events || []}
                onClose={() => setDayListModal(null)}
                onEventClick={handleEventClick}
                onAddClick={handleAddClick}
            />

            {/* 일정 상세(읽기전용) — 클릭하면 먼저 이게 뜬다. 작성자만 [수정]·[삭제]가 보인다. */}
            <EventDetailModal
                open={detailOpen}
                event={dialogEvent}
                canEdit={dialogEvent?._isOwner === true}
                onClose={() => setDetailOpen(false)}
                onEdit={handleEditFromDetail}
                onDelete={async () => {
                    await handleDelete(dialogEvent?.id);
                    setDetailOpen(false);
                }}
            />

            {/* 일정 추가/수정 다이얼로그 */}
            <EventDialog
                open={dialogOpen}
                event={dialogEvent}
                myDeptId={me?.deptId}
                myDeptName={me?.deptName}
                employees={employees}
                onSave={handleSave}
                onDelete={handleDelete}
                onClose={() => setDialogOpen(false)}
                onDone={() => {
                    setDialogOpen(false);
                    loadEvents();
                }}
            />
        </Box>
    );
}
